/*
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 UNESP Universidade Estadual Paulista "Júlio de Mesquita Filho"
 *
 */

import { Component, OnInit } from '@angular/core'
import { FormControl, FormGroup, Validators } from '@angular/forms'
import { InscricaoService } from 'src/app/services/inscricao.service'
import { ActivatedRoute } from '@angular/router'
import { ORDERED_SEXO_OPTIONS, Sexo } from 'src/app/enums/sexo'
import { EstadoCivil } from 'src/app/enums/estado-civil'
import { Estado } from 'src/app/enums/estado'
import { OrgaoExpedicaoRg } from 'src/app/enums/orgao-expedicao-rg'
import { InscricaoFicha } from 'src/app/models/inscricao-ficha'
import { GrauEscolaridade } from 'src/app/enums/grau-escolaridade'
import { Estrangeiro } from 'src/app/enums/estrangeiro'
import { Deficiencia } from 'src/app/enums/deficiencia'
import { CidadeService } from 'src/app/services/cidade.service'
import { DataService } from 'src/app/services/data.service'
import { ValidacaoService } from 'src/app/services/validacao.service'
import { ToastService } from '../../toast/services/toast.service'
import { Subject } from 'rxjs'
import { DownloadService } from 'src/app/services/download.service'
import { Autodeclaracao } from 'src/app/enums/autodeclaracao'
import { ConcursoService } from 'src/app/services/concurso.service'
import { TipoConcurso } from 'src/app/enums/tipo-concurso'
import { TranslateService } from '@ngx-translate/core'
import { environment } from 'src/environments/environment'

@Component({
  selector: 'app-candidato-inscricao-ficha',
  templateUrl: './candidato-inscricao-ficha.component.html',
  styleUrls: ['./candidato-inscricao-ficha.component.css'],
})
export class CandidatoInscricaoFichaComponent implements OnInit {
  [x: string]: any
  eventsSubject: Subject<void> = new Subject<void>()

  sexo = Sexo
  sexoOptions = ORDERED_SEXO_OPTIONS
  estadoCivil = EstadoCivil
  estado = Estado
  orgaoExpedicaoRg = OrgaoExpedicaoRg
  grauEscolaridade = GrauEscolaridade
  estrangeiro = Estrangeiro
  deficiencia = Deficiencia
  cidades: string[] = []
  temas: string[] = []

  id?: number
  inscricaoFicha?: InscricaoFicha
  tipoConcurso?: TipoConcurso
  permiteEdicao: boolean = false
  temComplemento: boolean = false
  mostrarRecursosEspeciaisProva: boolean = false

  nomeSocial: boolean = false
  nomeMae: boolean = true
  nomePai: boolean = true
  autodeclaracao = Autodeclaracao
  autodeclaraRaca: boolean = false
  declaracaoVeracidade: boolean = false
  pontuacaoDiferenciada: boolean = false
  mostrarCamposAutodeclaracao: boolean = false
  enderecoNacional: boolean = false
  enableTelaContato: string = environment.enableTelaContato
  showIdentidadeGenero: boolean = false

  form = new FormGroup({
    idConcurso: new FormControl(),
    nome: new FormControl('', Validators.required),
    dataNascimento: new FormControl('', Validators.required),
    nomeSocial: new FormControl({ value: '', disabled: true }),
    nomeMae: new FormControl('', Validators.required),
    nomePai: new FormControl('', Validators.required),
    profissao: new FormControl('', Validators.required),
    escolaridade: new FormControl('', Validators.required),
    sexo: new FormControl('', Validators.required),
    estadoCivil: new FormControl('', Validators.required),
    estrangeiro: new FormControl('', Validators.required),
    cpf: new FormControl({ value: '', disabled: true }),
    passaporte: new FormControl({ value: '', disabled: true }),
    rne: new FormControl({ value: '', disabled: true }),
    rg: new FormControl({ value: '', disabled: true }),
    uf: new FormControl({ value: '', disabled: true }),
    dataExpedicao: new FormControl({ value: '', disabled: true }),
    orgaoExpedicao: new FormControl({ value: '', disabled: true }),
    pais: new FormControl({ value: '', disabled: true }, Validators.required),
    estado: new FormControl({ value: '', disabled: true }, Validators.required),
    cidade: new FormControl({ value: '', disabled: true }, Validators.required),
    celular: new FormControl('', Validators.required),
    telefone: new FormControl('', Validators.required),
    email: new FormControl('', Validators.required),
    enderecoNacional: new FormControl('', Validators.required),
    enderecoCep: new FormControl('', Validators.required),
    enderecoLogradouro: new FormControl('', Validators.required),
    enderecoNumero: new FormControl('', Validators.required),
    enderecoBairro: new FormControl('', Validators.required),
    enderecoCidade: new FormControl('', Validators.required),
    enderecoEstado: new FormControl('', Validators.required),
    enderecoComplemento: new FormControl(''),
    concorrerVagaDeficiente: new FormControl('', Validators.required),
    canhoto: new FormControl('', Validators.required),
    lactante: new FormControl('', Validators.required),
    possuiOutraNecessidade: new FormControl('', Validators.required),
    atendimentoEspecialLaudoMedico: new FormControl(),
    atendimentoEspecialNecessidadesProva: new FormControl(),
    autodeclaraRaca: new FormControl(false, Validators.required),
    pontuacaoDiferenciada: new FormControl(false, Validators.required),
    autodeclaracao: new FormControl('NAO_INFORMADO', Validators.required),
    declaracaoVeracidade: new FormControl(false, Validators.requiredTrue),
    possuiDeficiencia: new FormControl('', Validators.required),
    deficiencia: new FormControl(),
    deficienciaNecessidadesProva: new FormControl(),
    temas: new FormControl(),
    jurado: new FormControl('', Validators.required),
    provaIngles: new FormControl(),
    identidadeGenero: new FormControl(''),
    documentoJurado: new FormControl()
  })

  i18n = {
    atencao: '',
    parabens: '',
    erroFormulario: '',
    sucessoInscricao: '',
  }

  constructor(
    private inscricaoService: InscricaoService,
    private cidadeService: CidadeService,
    private dataService: DataService,
    private route: ActivatedRoute,
    private validacaoService: ValidacaoService,
    private toastService: ToastService,
    private downloadService: DownloadService,
    private concursoService: ConcursoService,
    private readonly translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.carregarI18n()
    this.translateService.onLangChange.subscribe(() => {
      this.carregarI18n()
    })
    
    this.route.params.subscribe(params => {
      this.id = params['id']
      this.form.reset()        
      if (this.id) {
        this.inscricaoService.get(this.id).subscribe(obj => {
          // FIXME: verificar se não é melhor obtem junto com os dados da inscrição
          this.inscricaoService.temas(obj.id).subscribe(temas => {
            this.temas = temas
          })
          this.inscricaoFicha = obj
          this.temComplemento = obj.inscricaoComplementoFase1.length > 0
          this.form.get('idConcurso')?.setValue(obj.idConcurso)
          this.tipoConcurso = obj.tipoConcurso
          this.form.get('nome')?.setValue(obj.nome)
          this.form.get('dataNascimento')?.setValue(this.dataService.formatToBr(obj.dataNascimento))
          this.form.get('nomeSocial')?.setValue(obj.nomeSocial)
          this.form.get('nomeMae')?.setValue(obj.nomeMae)
          this.form.get('nomePai')?.setValue(obj.nomePai)
          this.form.get('profissao')?.setValue(obj.profissao)
          this.form.get('escolaridade')?.setValue(obj.escolaridade)
          this.form.get('sexo')?.setValue(obj.sexo)
          this.form.get('identidadeGenero')?.setValue(obj.identidadeGenero)
          if (obj.sexo === Sexo.OUTRO.toUpperCase()) {
            this.showIdentidadeGenero = true
          }
          this.form.get('estadoCivil')?.setValue(obj.estadoCivil)
          this.form.get('estrangeiro')?.setValue(obj.estrangeiro)
          this.form.get('cpf')?.setValue(obj.cpf)
          this.form.get('passaporte')?.setValue(obj.passaporte)
          this.form.get('rne')?.setValue(obj.rne)
          this.form.get('rg')?.setValue(obj.rg)
          this.form.get('uf')?.setValue(obj.uf)
          this.form.get('dataExpedicao')?.setValue(this.dataService.formatToBr(obj.dataExpedicao))
          this.form.get('orgaoExpedicao')?.setValue(obj.orgaoExpedicao)
          this.form.get('pais')?.setValue(obj.pais)
          this.form.get('estado')?.setValue(obj.estado)
          this.form.get('cidade')?.setValue(obj.cidade)
          this.form.get('celular')?.setValue(obj.celular)
          this.form.get('telefone')?.setValue(obj.telefone)
          this.form.get('email')?.setValue(obj.email)
          this.form.get('enderecoCep')?.setValue(obj.enderecoCep)
          this.form.get('enderecoLogradouro')?.setValue(obj.enderecoLogradouro)
          this.form.get('enderecoNumero')?.setValue(obj.enderecoNumero)
          this.form.get('enderecoBairro')?.setValue(obj.enderecoBairro)
          this.form.get('enderecoCidade')?.setValue(obj.enderecoCidade)
          this.form.get('enderecoEstado')?.setValue(obj.enderecoEstado)
          this.form.get('enderecoComplemento')?.setValue(obj.enderecoComplemento)
          this.form.get('canhoto')?.setValue(obj.canhoto ? 'true' : 'false')
          this.form.get('lactante')?.setValue(obj.lactante ? 'true' : 'false')
          this.form.get('concorrerVagaDeficiente')?.setValue(obj.concorrerVagaDeficiente ? 'true' : 'false')
          this.form.get('possuiOutraNecessidade')?.setValue(obj.possuiOutraNecessidade ? 'true' : 'false')
          this.form.get('atendimentoEspecialNecessidadesProva')?.setValue(obj.atendimentoEspecialNecessidadesProva)
          this.form.get('jurado')?.setValue(obj.jurado ? 'true' : 'false')
          this.form.get('provaIngles')?.setValue(obj.provaIngles ? 'true' : 'false')
          if (this.tipoConcurso.match('TECADM')) {
            this.form.get('autodeclaraRaca')?.setValue(!!obj.autodeclaraRaca)
            this.form.get('autodeclaracao')?.setValue(obj.autodeclaracao)
            this.form.get('pontuacaoDiferenciada')?.setValue(!!obj.pontuacaoDiferenciada)
            this.form.get('declaracaoVeracidade')?.setValue(!!obj.declaracaoVeracidade)
          } else {
            this.form.get('autodeclaraRaca')?.disable()
            this.form.get('pontuacaoDiferenciada')?.disable()
            this.form.get('autodeclaracao')?.disable()
            this.form.get('declaracaoVeracidade')?.disable()
          }

          this.form.get('possuiDeficiencia')?.setValue(obj.deficiencia.length ? 'true' : 'false')
          this.form.get('deficiencia')?.setValue(obj.deficiencia)
          this.form.get('deficienciaNecessidadesProva')?.setValue(obj.deficienciaNecessidadesProva)
          this.form.get('temas')?.setValue(obj.temas)
          this.selecionaEstado()
          if (!this.permiteEdicao) {
            this.form.disable()
          }
          this.validaAtendimentoEspecial()          
        })
      }
    })
  }

  applyTouchedToInvalidFields() {
    for (const name in this.form.controls) {
      if (this.form.get(name)?.invalid) {
        this.form.get(name)?.markAsTouched()
      }
    }
  }

  gravar(): void {    
    if (this.permiteEdicao) {
      this.applyTouchedToInvalidFields()

      if (!this.form.valid) {
        this.toastService.showErrorToast(this.i18n.atencao, this.i18n.erroFormulario)
        return
      }
      if (this.id) {
        const formularioTemporario = this.form.value
        const { enderecoNacional, ...formularioFinal } = formularioTemporario

        //let formulario = <InscricaoFicha>this.form.value
        let formulario = <InscricaoFicha>formularioFinal

        //let formulario = <InscricaoFicha>this.form.value

        this.inscricaoService.alterar(this.id, formulario).subscribe(inscricaoFicha => {
          this.toastService.showSuccessToast(this.i18n.parabens, this.i18n.sucessoInscricao)
        })
      }
    }
  }

  algumCampoAtendimentoEspecialHabilitado(): boolean {
    const lactante = this.form.get('lactante')?.value === 'true'
    const canhoto = this.form.get('canhoto')?.value === 'true'
    const possuiOutraNecessidade = this.form.get('possuiOutraNecessidade')?.value === 'true'

    return lactante || canhoto || possuiOutraNecessidade
  }

  print(): void {
    window.print()
  }

  validaEstrangeiro(): void {
    this.validacaoService.validaEstrangeiro(this.form)
  }

  validaAtendimentoEspecial(): void {
    const algumHabilitado = this.algumCampoAtendimentoEspecialHabilitado()
    this.mostrarRecursosEspeciaisProva = algumHabilitado
  }

  validaDeficiencia(): void {
    this.validacaoService.validaDeficiencia(this.form)
  }

  validaJurado(): void {
    this.validacaoService.validaJurado(this.form)
  }

  selecionaEstado(): void {
    const estado = this.form.get('estado')?.value
    this.cidades = this.cidadeService.get(estado ?? '')
  }

  validaAutodeclaraRaca(): void {
    this.validacaoService.validaAutodeclaraRaca(this.form)
  }

  onStateChange(): void {
    this.eventsSubject.next()
  }

  validaCEP(enderecoNacional: boolean): void {
    this.validacaoService.validaCEP(this.form, enderecoNacional)
  }

  comprovanteEstudante(): void {
    this.downloadService.comprovanteEstudante(this.inscricaoFicha?.id ?? 0)
  }

  comprovanteRenda(): void {
    this.downloadService.comprovanteRenda(this.inscricaoFicha?.id ?? 0)
  }

  documentoIdentificacao(): void {
    this.downloadService.documentoIdentificacao(this.inscricaoFicha?.id ?? 0)
  }

  requerimentoNomeSocial(): void {
    this.downloadService.requerimentoNomeSocial(this.inscricaoFicha?.id ?? 0)
  }

  deficienciaLaudoMedico(): void {
    this.downloadService.deficienciaLaudoMedico(this.inscricaoFicha?.id ?? 0)
  }

  atendimentoEspecialLaudoMedico(): void {
    this.downloadService.atendimentoEspecialLaudoMedico(this.inscricaoFicha?.id ?? 0)
  }

  save(name: string) {
    localStorage.setItem(name, this.form.get(name)?.getRawValue())
  }

  alteraNomeSocial(): void {
    this.nomeSocial = !this.nomeSocial
    this.validaNomeSocial()
  }

  validaNomeSocial(): void {
    this.validacaoService.validaNomeSocial(this.form, this.nomeSocial)
    localStorage.setItem('chkNomeSocial', this.nomeSocial.toString())
  }

  alteraMaeDesconhecida(): void {
    this.nomeMae = !this.nomeMae
    this.validaMaeDesconhecida()
  }

  validaMaeDesconhecida(): void {
    this.validacaoService.validaMaeDesconhecida(this.form, this.nomeMae)
    localStorage.setItem('chkNomeMae', this.nomeMae.toString())
  }

  alteraPaiDesconhecido(): void {
    this.nomePai = !this.nomePai
    this.validaPaiDesconhecido()
  }

  validaPaiDesconhecido(): void {
    this.validacaoService.validaPaiDesconhecido(this.form, this.nomePai)
    localStorage.setItem('chkNomePai', this.nomePai.toString())
  }

  alteraTipoEndereco(): void {
    this.enderecoNacional = this.form.controls['enderecoNacional'].value === 'true' ? true : false
  }

  private carregarI18n(): void {
    this.translateService
      .get(['shared.atencao', 'shared.parabens', 'shared.erro_formulario', 'ficha_inscricao_shared.sucesso_inscricao'])
      .subscribe(traducoes => {
        this.i18n.atencao = traducoes['shared.atencao']
        this.i18n.parabens = traducoes['shared.parabens']
        this.i18n.erroFormulario = traducoes['shared.erro_formulario']
        this.i18n.sucessoInscricao = traducoes['ficha_inscricao_shared.sucesso_inscricao']
      })
  }
}
